import React, { useEffect, useState } from "react";
import { Button, Form, Alert, Badge } from "react-bootstrap";
import { listServices, createService, updateService, deleteService } from "../services/partnerAuthService";

const getErr = (err, fallback) => err.response?.data?.error || fallback;

const emptyForm = { category: "", title: "", description: "", destinationCities: "", basePrice: "" };
const emptyTaxiDetails = {
  vehicleType: "", vehicleModel: "", seatingCapacity: "", luggageCapacity: "", isAC: true, driverIncluded: true,
  serviceArea: "", tripTypes: [], perKmPrice: "", perHourPrice: "", includedKm: "", extraKmCharge: "",
};
const emptyRefundPolicy = { freeCancellationHours: 24, partialRefundPercent: 50, nonRefundableHours: 6 };

const VEHICLE_TYPE_OPTIONS = ["Hatchback", "Sedan", "SUV", "Tempo Traveller", "Other"];
const TRIP_TYPES = [
  { value: "AirportTransfer", label: "Airport Transfer" },
  { value: "Local", label: "Local" },
  { value: "Outstation", label: "Outstation" },
  { value: "OneWay", label: "One Way" },
  { value: "RoundTrip", label: "Round Trip" },
  { value: "Hourly", label: "Hourly" },
  { value: "FullDay", label: "Full Day" },
];

const isTaxiCategory = (category) => /taxi|cab/i.test(category || "");

const APPROVAL_TONE = { Pending: "secondary", Approved: "success", Rejected: "danger", ChangesRequested: "warning" };
const APPROVAL_LABEL = { Pending: "Pending review", Approved: "Approved", Rejected: "Rejected", ChangesRequested: "Changes requested" };

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [taxiDetails, setTaxiDetails] = useState(emptyTaxiDetails);
  const [refundPolicy, setRefundPolicy] = useState(emptyRefundPolicy);
  const [editingId, setEditingId] = useState(null);
  const [editingWasApproved, setEditingWasApproved] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [vehicleDocFiles, setVehicleDocFiles] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [existingDocs, setExistingDocs] = useState([]);

  const load = () => {
    setLoading(true);
    listServices()
      .then(({ data }) => setServices(data.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const resetForm = () => {
    setForm(emptyForm);
    setTaxiDetails(emptyTaxiDetails);
    setRefundPolicy(emptyRefundPolicy);
    setEditingId(null);
    setEditingWasApproved(false);
    setPhotoFiles([]);
    setVehicleDocFiles([]);
    setExistingPhotos([]);
    setExistingDocs([]);
  };

  const startAdd = () => {
    resetForm();
    setShowForm(true);
    setError("");
  };

  const cancelForm = () => {
    resetForm();
    setShowForm(false);
  };

  const startEdit = (service) => {
    setForm({
      category: service.category || "",
      title: service.title || "",
      description: service.description || "",
      destinationCities: (service.destinationCities || []).join(", "),
      basePrice: service.basePrice ?? "",
    });
    setTaxiDetails({
      vehicleType: service.taxiDetails?.vehicleType || "",
      vehicleModel: service.taxiDetails?.vehicleModel || "",
      seatingCapacity: service.taxiDetails?.seatingCapacity ?? "",
      luggageCapacity: service.taxiDetails?.luggageCapacity ?? "",
      isAC: service.taxiDetails?.isAC ?? true,
      driverIncluded: service.taxiDetails?.driverIncluded ?? true,
      serviceArea: service.taxiDetails?.serviceArea || "",
      tripTypes: service.taxiDetails?.tripTypes || [],
      perKmPrice: service.taxiDetails?.perKmPrice || "",
      perHourPrice: service.taxiDetails?.perHourPrice || "",
      includedKm: service.taxiDetails?.includedKm || "",
      extraKmCharge: service.taxiDetails?.extraKmCharge || "",
    });
    setRefundPolicy({
      freeCancellationHours: service.refundPolicy?.freeCancellationHours ?? 24,
      partialRefundPercent: service.refundPolicy?.partialRefundPercent ?? 50,
      nonRefundableHours: service.refundPolicy?.nonRefundableHours ?? 6,
    });
    setPhotoFiles([]);
    setVehicleDocFiles([]);
    setExistingPhotos(service.photos || []);
    setExistingDocs(service.taxiDetails?.vehicleDocuments || []);
    setEditingId(service._id);
    setEditingWasApproved(service.approvalStatus === "Approved");
    setShowForm(true);
    setError("");
    setNotice("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleTripType = (value) => {
    setTaxiDetails((td) => ({
      ...td,
      tripTypes: td.tripTypes.includes(value) ? td.tripTypes.filter((t) => t !== value) : [...td.tripTypes, value],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    if (!form.category || !form.title) return setError("Category and title are required");

    const taxi = isTaxiCategory(form.category);
    if (taxi && (!taxiDetails.vehicleType || !taxiDetails.seatingCapacity || taxiDetails.tripTypes.length === 0)) {
      return setError("For a taxi/cab service, vehicle type, seating capacity and at least one trip type are required");
    }

    const cleanTaxiDetails = taxi
      ? {
          ...taxiDetails,
          seatingCapacity: Number(taxiDetails.seatingCapacity) || null,
          luggageCapacity: taxiDetails.luggageCapacity ? Number(taxiDetails.luggageCapacity) : null,
          perKmPrice: Number(taxiDetails.perKmPrice) || 0,
          perHourPrice: Number(taxiDetails.perHourPrice) || 0,
          includedKm: Number(taxiDetails.includedKm) || 0,
          extraKmCharge: Number(taxiDetails.extraKmCharge) || 0,
        }
      : null;
    const cleanRefundPolicy = {
      freeCancellationHours: Number(refundPolicy.freeCancellationHours) || 0,
      partialRefundPercent: Math.min(100, Math.max(0, Number(refundPolicy.partialRefundPercent) || 0)),
      nonRefundableHours: Number(refundPolicy.nonRefundableHours) || 0,
    };

    setSaving(true);
    try {
      if (editingId) {
        // PATCH /partner/services/:id has no upload middleware server-side (matches
        // updatePackage's pattern) — plain JSON payload, no new photos/documents on edit.
        const payload = { ...form };
        if (taxi) payload.taxiDetails = JSON.stringify(cleanTaxiDetails);
        payload.refundPolicy = JSON.stringify(cleanRefundPolicy);
        await updateService(editingId, payload);
        if (editingWasApproved) {
          setNotice("Service updated. Your changes will be reviewed again before going live.");
        } else {
          setNotice("Service updated.");
        }
      } else {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        if (taxi) fd.append("taxiDetails", JSON.stringify(cleanTaxiDetails));
        fd.append("refundPolicy", JSON.stringify(cleanRefundPolicy));
        photoFiles.forEach((file) => fd.append("photos", file));
        if (taxi) vehicleDocFiles.forEach((file) => fd.append("vehicleDocuments", file));
        await createService(fd);
        setNotice("Service submitted for review.");
      }
      resetForm();
      setShowForm(false);
      load();
    } catch (err) {
      setError(getErr(err, editingId ? "Failed to update service" : "Failed to create service"));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (service) => {
    try {
      await updateService(service._id, { isActive: !service.isActive });
      load();
    } catch (err) {
      setError(getErr(err, "Failed to update service"));
    }
  };

  const remove = async (service) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await deleteService(service._id);
      load();
    } catch (err) {
      setError(getErr(err, "Failed to delete service"));
    }
  };

  if (loading) return <p>Loading services…</p>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">My Services</h4>
        <Button size="sm" onClick={() => (showForm ? cancelForm() : startAdd())}>
          {showForm ? "Cancel" : "+ Add Service"}
        </Button>
      </div>
      {error && <Alert variant="danger">{error}</Alert>}
      {notice && <Alert variant="info" onClose={() => setNotice("")} dismissible>{notice}</Alert>}

      {showForm && (
        <div className="partner-card mb-4">
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-2">
              <Form.Label>Category</Form.Label>
              <Form.Control placeholder="e.g. Airport Pickup, Sedan Rental, Taxi" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              <Form.Text muted>Categories containing "taxi" or "cab" unlock the vehicle details section below.</Form.Text>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Title</Form.Label>
              <Form.Control placeholder="e.g. Goa Airport Pickup" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Destination Cities (comma separated)</Form.Label>
              <Form.Control value={form.destinationCities} onChange={(e) => setForm({ ...form, destinationCities: e.target.value })} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Base Price (₹)</Form.Label>
              <Form.Control type="number" value={form.basePrice} onChange={(e) => setForm({ ...form, basePrice: e.target.value })} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Photos</Form.Label>
              {editingId ? (
                <>
                  <Form.Text muted className="d-block">
                    {existingPhotos.length ? `${existingPhotos.length} photo(s) already uploaded.` : "No photos uploaded yet."}
                    {" "}Photos can only be added when the service is first created.
                  </Form.Text>
                </>
              ) : (
                <Form.Control
                  type="file" accept="image/*" multiple
                  onChange={(e) => setPhotoFiles(Array.from(e.target.files || []))}
                />
              )}
            </Form.Group>

            {isTaxiCategory(form.category) && (
              <div className="partner-card mb-3" style={{ background: "#f9fafb" }}>
                <h6 className="mb-3">Taxi / Vehicle Details</h6>
                <Form.Group className="mb-2">
                  <Form.Label>Vehicle Type</Form.Label>
                  <Form.Control
                    list="vehicle-type-options"
                    placeholder="e.g. Sedan"
                    value={taxiDetails.vehicleType}
                    onChange={(e) => setTaxiDetails({ ...taxiDetails, vehicleType: e.target.value })}
                  />
                  <datalist id="vehicle-type-options">
                    {VEHICLE_TYPE_OPTIONS.map((v) => <option key={v} value={v} />)}
                  </datalist>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Vehicle Model</Form.Label>
                  <Form.Control placeholder="e.g. Swift Dzire" value={taxiDetails.vehicleModel} onChange={(e) => setTaxiDetails({ ...taxiDetails, vehicleModel: e.target.value })} />
                </Form.Group>
                <div className="d-flex gap-3 flex-wrap">
                  <Form.Group className="mb-2" style={{ minWidth: 160 }}>
                    <Form.Label>Seating Capacity</Form.Label>
                    <Form.Control type="number" min={1} value={taxiDetails.seatingCapacity} onChange={(e) => setTaxiDetails({ ...taxiDetails, seatingCapacity: e.target.value })} />
                  </Form.Group>
                  <Form.Group className="mb-2" style={{ minWidth: 160 }}>
                    <Form.Label>Luggage Capacity (bags)</Form.Label>
                    <Form.Control type="number" min={0} value={taxiDetails.luggageCapacity} onChange={(e) => setTaxiDetails({ ...taxiDetails, luggageCapacity: e.target.value })} />
                  </Form.Group>
                  <Form.Group className="mb-2 d-flex align-items-end">
                    <Form.Check
                      type="switch"
                      id="taxi-is-ac"
                      label="Air Conditioned"
                      checked={!!taxiDetails.isAC}
                      onChange={(e) => setTaxiDetails({ ...taxiDetails, isAC: e.target.checked })}
                    />
                  </Form.Group>
                  <Form.Group className="mb-2 d-flex align-items-end">
                    <Form.Check
                      type="switch"
                      id="taxi-driver-included"
                      label="Driver Included"
                      checked={!!taxiDetails.driverIncluded}
                      onChange={(e) => setTaxiDetails({ ...taxiDetails, driverIncluded: e.target.checked })}
                    />
                  </Form.Group>
                </div>
                <Form.Group className="mb-2">
                  <Form.Label>Service Area</Form.Label>
                  <Form.Control placeholder="e.g. Goa & surrounding areas" value={taxiDetails.serviceArea} onChange={(e) => setTaxiDetails({ ...taxiDetails, serviceArea: e.target.value })} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Trip Types</Form.Label>
                  <div className="d-flex flex-wrap gap-3">
                    {TRIP_TYPES.map((t) => (
                      <Form.Check
                        key={t.value}
                        type="checkbox"
                        id={`trip-type-${t.value}`}
                        label={t.label}
                        checked={taxiDetails.tripTypes.includes(t.value)}
                        onChange={() => toggleTripType(t.value)}
                      />
                    ))}
                  </div>
                </Form.Group>

                <h6 className="mb-2">Metered Pricing (optional — leave 0 for a flat Base Price)</h6>
                <Form.Text muted className="d-block mb-2">
                  Use either "Per KM Price" for straight metered trips, OR "Included KM" + "Extra KM Charge" for a
                  package-style rate (e.g. 80km/day included, then charged extra). Don't set both.
                </Form.Text>
                <div className="d-flex gap-3 flex-wrap mb-2">
                  <Form.Group style={{ minWidth: 160 }}>
                    <Form.Label>Per KM Price (₹)</Form.Label>
                    <Form.Control type="number" min={0} value={taxiDetails.perKmPrice} onChange={(e) => setTaxiDetails({ ...taxiDetails, perKmPrice: e.target.value })} />
                  </Form.Group>
                  <Form.Group style={{ minWidth: 160 }}>
                    <Form.Label>Per Hour Price (₹)</Form.Label>
                    <Form.Control type="number" min={0} value={taxiDetails.perHourPrice} onChange={(e) => setTaxiDetails({ ...taxiDetails, perHourPrice: e.target.value })} />
                    <Form.Text muted>For Hourly / Full Day trip types.</Form.Text>
                  </Form.Group>
                  <Form.Group style={{ minWidth: 160 }}>
                    <Form.Label>Included KM</Form.Label>
                    <Form.Control type="number" min={0} value={taxiDetails.includedKm} onChange={(e) => setTaxiDetails({ ...taxiDetails, includedKm: e.target.value })} />
                  </Form.Group>
                  <Form.Group style={{ minWidth: 160 }}>
                    <Form.Label>Extra KM Charge (₹)</Form.Label>
                    <Form.Control type="number" min={0} value={taxiDetails.extraKmCharge} onChange={(e) => setTaxiDetails({ ...taxiDetails, extraKmCharge: e.target.value })} />
                  </Form.Group>
                </div>

                <Form.Group className="mb-1">
                  <Form.Label>Vehicle Documents (RC, insurance, permit, etc.)</Form.Label>
                  {editingId ? (
                    <Form.Text muted className="d-block">
                      {existingDocs.length ? `${existingDocs.length} document(s) already uploaded.` : "No documents uploaded yet."}
                      {" "}Documents can only be added when the service is first created.
                    </Form.Text>
                  ) : (
                    <Form.Control
                      type="file" accept="image/*,.pdf" multiple
                      onChange={(e) => setVehicleDocFiles(Array.from(e.target.files || []))}
                    />
                  )}
                </Form.Group>
              </div>
            )}

            <div className="partner-card mb-3" style={{ background: "#f9fafb" }}>
              <h6 className="mb-3">Cancellation Policy</h6>
              <Form.Group className="mb-2">
                <Form.Label>Free cancellation up to (hours before trip)</Form.Label>
                <Form.Control
                  type="number" min={0}
                  value={refundPolicy.freeCancellationHours}
                  onChange={(e) => setRefundPolicy({ ...refundPolicy, freeCancellationHours: e.target.value })}
                />
                <Form.Text muted>Customer gets a full refund if they cancel this many hours before the trip.</Form.Text>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Partial refund (%)</Form.Label>
                <Form.Control
                  type="number" min={0} max={100}
                  value={refundPolicy.partialRefundPercent}
                  onChange={(e) => setRefundPolicy({ ...refundPolicy, partialRefundPercent: e.target.value })}
                />
                <Form.Text muted>Percentage refunded for cancellations after the free window but before the non-refundable window.</Form.Text>
              </Form.Group>
              <Form.Group className="mb-1">
                <Form.Label>No refund within (hours of trip)</Form.Label>
                <Form.Control
                  type="number" min={0}
                  value={refundPolicy.nonRefundableHours}
                  onChange={(e) => setRefundPolicy({ ...refundPolicy, nonRefundableHours: e.target.value })}
                />
                <Form.Text muted>Cancellations inside this window are not refunded.</Form.Text>
              </Form.Group>
            </div>

            <Button type="submit" disabled={saving}>{saving ? "Saving…" : editingId ? "Save Changes" : "Save Service"}</Button>
          </Form>
        </div>
      )}

      {services.length === 0 ? (
        <div className="partner-card partner-empty">Add your first service to start receiving relevant customer requests.</div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {services.map((s) => (
            <div key={s._id} className="partner-card">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h6 className="mb-1">
                    {s.title}{" "}
                    <Badge bg={s.isActive ? "success" : "secondary"}>{s.isActive ? "Active" : "Paused"}</Badge>{" "}
                    <Badge bg={APPROVAL_TONE[s.approvalStatus] || "secondary"}>{APPROVAL_LABEL[s.approvalStatus] || s.approvalStatus}</Badge>
                  </h6>
                  <small className="text-muted">{s.category} · ₹{s.basePrice} · {(s.destinationCities || []).join(", ") || "Any destination"}</small>
                </div>
                <div className="d-flex gap-2">
                  <Button size="sm" variant="outline-primary" onClick={() => startEdit(s)}>Edit</Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => toggleActive(s)}>{s.isActive ? "Pause" : "Resume"}</Button>
                  <Button size="sm" variant="outline-danger" onClick={() => remove(s)}>Delete</Button>
                </div>
              </div>

              {s.approvalStatus === "ChangesRequested" && (
                <Alert variant="warning" className="mt-3 mb-0">
                  <strong>Admin requested changes:</strong>{" "}
                  {s.approvalRemark || "Please review and update this listing."}
                  <div className="mt-2">
                    <Button size="sm" variant="warning" onClick={() => startEdit(s)}>Edit &amp; Resubmit</Button>
                  </div>
                </Alert>
              )}
              {s.approvalStatus === "Rejected" && (
                <Alert variant="danger" className="mt-3 mb-0">
                  <strong>Rejected:</strong> {s.approvalRemark || "This listing did not meet approval requirements."}
                  <div className="mt-2">
                    <Button size="sm" variant="outline-danger" onClick={() => startEdit(s)}>Edit &amp; Resubmit</Button>
                  </div>
                </Alert>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
